import { useEffect, useRef, useState } from "react";
import { Lock, ShieldCheck } from "lucide-react";
import { getMollie } from "../../lib/mollieCheckout";

const FIELDS = [
  { type: "cardNumber", id: "mc-card-number", fr: "Numéro de carte", en: "Card number", span: 2 },
  { type: "expiryDate", id: "mc-expiry", fr: "Date d'expiration", en: "Expiry date", span: 1 },
  { type: "verificationCode", id: "mc-cvc", fr: "Code de sécurité", en: "Security code", span: 1 },
  { type: "cardHolder", id: "mc-holder", fr: "Nom sur la carte", en: "Name on card", span: 2 },
];

const STYLES = {
  base: { color: "#1c1917", fontSize: "15px", fontWeight: "400", letterSpacing: "0.3px", "::placeholder": { color: "#a8a29e" } },
  valid: { color: "#1c1917" },
  invalid: { color: "#b91c1c" },
};

// Champs carte cryptés Mollie Components : la carte ne transite jamais par nos serveurs
export function MollieCardFields({ cfg, en, onReady }) {
  const comps = useRef([]);
  const [errors, setErrors] = useState({});
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const mollie = await getMollie(cfg.profile_id, cfg.testmode, en ? "en_US" : "fr_FR");
      if (!alive) return;
      comps.current = FIELDS.map((f) => {
        const c = mollie.createComponent(f.type, { styles: STYLES });
        c.mount(`#${f.id}`);
        c.addEventListener("change", (e) => setErrors((p) => ({ ...p, [f.type]: e.error && e.touched ? e.error : "" })));
        c.addEventListener("focus", () => setFocus(f.type));
        c.addEventListener("blur", () => setFocus(null));
        return c;
      });
      onReady(() => mollie.createToken());
    })().catch(() => onReady(null));
    return () => {
      alive = false;
      comps.current.forEach((c) => { try { c.unmount(); } catch { /* déjà démonté */ } });
      comps.current = [];
    };
  }, [cfg.profile_id, cfg.testmode, en, onReady]);

  return (
    <div className="space-y-3" data-testid="mollie-card-fields">
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map((f) => (
          <div key={f.type} className={f.span === 2 ? "col-span-2" : "col-span-1"}>
            <div className={`lux-field ${focus === f.type ? "lux-field-focus" : ""} ${errors[f.type] ? "lux-field-error" : ""}`}>
              <span className="lux-label">{en ? f.en : f.fr}</span>
              <div id={f.id} className="h-6" data-testid={`mollie-${f.type}`} />
            </div>
            {errors[f.type] && <p className="text-[11px] text-red-700 mt-1" data-testid={`mollie-${f.type}-error`}>{errors[f.type]}</p>}
          </div>
        ))}
      </div>
      <div className="flex gap-2 text-[12px] text-stone-600 leading-relaxed pt-1">
        <ShieldCheck className="w-4 h-4 text-[#9a7b4f] shrink-0 mt-0.5" />
        <p>
          <strong className="text-stone-800">{en ? "100% secure payment." : "Paiement 100 % sécurisé."}</strong>{" "}
          {en ? "Your card details are entered in encrypted fields provided by Mollie. They never pass through our servers."
            : "Vos informations bancaires sont saisies dans des champs cryptés fournis par Mollie. Elles ne transitent jamais par nos serveurs."}
        </p>
      </div>
      <div className="flex items-center gap-5 border-t border-stone-200 pt-3 text-[10px] tracking-[0.18em] uppercase text-stone-500">
        <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" />{en ? "256-bit SSL encryption" : "Chiffrement SSL 256 bits"}</span>
        <span>{en ? "3D Secure authentication" : "Authentification 3D Secure"}</span>
      </div>
    </div>
  );
}
